// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { Styles, StylesProvider } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { breakpointMediaQueries, injectCss, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xHeaderUpdate } from "@vanmeter/App/Handlers/XNG/Header";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { isCheckoutPage } from "@vanmeter/App/Store/Selectors/CheckoutSelector";
import {
    getCartLinkWidget,
    getHeaderSignInWidget,
    getLogoImageWidget,
    getMainNavigationWidget,
} from "@vanmeter/App/Store/Selectors/HeaderSelectors";
import SearchInput, { SearchInputStyles } from "@vanmeter/Overrides/Widgets/Header/SearchInput";
import Search from "@vanmeter/XDL/Icons/IconSearch";
import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import styled, { css, ThemeProps, withTheme } from "styled-components";

// <XNG-Changes>
const mapStateToProps = (state: CustomAppState) => {
    return {
        logoImageWidget: getLogoImageWidget(state),
        mainNavigationWidget: getMainNavigationWidget(state),
        headerSignInWidget: getHeaderSignInWidget(state),
        cartLinkWidget: getCartLinkWidget(state),
        mobileSearch: state.xHeader.mobileSearch,
        headerHeight: state.xHeader.headerHeight,
        isTablet: state.xCommon.isTabletScreen,
        isCheckout: isCheckoutPage(state),
        hideCompactHeader: state.xHeader.hideCompactHeader,
    };
};

const mapDispatchToProps = {
    xHeaderUpdate,
};
// </XNG-Changes>

type Props = PageProps &
    HasShellContext &
    ThemeProps<BaseTheme> &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CompactHeaderStyles {
    page?: MobiusPageProps;
    mainNavigationWrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    pageInnerWrapper?: InjectableCss;
    // XNG-Changes
    searchButton?: ButtonPresentationProps;
    rightSectionWrapper?: InjectableCss;
    mobileSearchInputStyles?: SearchInputStyles;
}

const boxShadowStyleMap: Dictionary<string> = {
    light: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
    medium: "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    dark: "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
};

function generateShadow(props: ThemeProps<BaseTheme>) {
    const { isDropShadow, dropShadowStyle } = props.theme.header;
    if (isDropShadow && dropShadowStyle in boxShadowStyleMap) {
        return `box-shadow: ${boxShadowStyleMap[dropShadowStyle]}`;
    }
    return "";
}

function generateBorder(props: ThemeProps<BaseTheme>) {
    const { isBorder, borderColor, borderThickness, isBorderTop, isBorderLeft, isBorderBottom, isBorderRight } =
        props.theme.header;
    if (!isBorder) {
        return "border: none;";
    }

    let border = `border: ${borderThickness}px solid ${resolveColor(borderColor, props.theme)};`;

    if (!isBorderTop) {
        border += "border-top: none;";
    }
    if (!isBorderBottom) {
        border += "border-bottom: none;";
    }
    if (!isBorderLeft) {
        border += "border-left: none;";
    }
    if (!isBorderRight) {
        border += "border-right: none;";
    }

    return border;
}

export const compactHeaderStyles: CompactHeaderStyles = {
    page: {
        padding: 0, // * Padding doesn't work with fullWidth because of logic on Line 64 if Page component
        fullWidth: [true, true, true, true, true], // make the header full width and control the margins with container css
        css: css`
            position: fixed;
            top: 0;
            left: 50%;
            z-index: 1;
            opacity: 0;
            transform: translateY(-100%) translateX(-50%);
            padding: ${props => props.theme.header.padding};
            ${generateBorder}
            ${generateShadow};
        `,
    },
    logoWrapper: {
        css: css`
            align-self: center;
        `,
    },
    searchWrapper: {
        css: css`
            align-self: center;
        `,
    },
    mainNavigationWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = compactHeaderStyles;

export const createStylesProviderStyles = (theme: BaseTheme) =>
    ({
        mainNavigation: {
            menuItemTypography: {
                css: css`
                    font-weight: 600;
                    white-space: nowrap;
                `,
                color: theme.header.linkColor,
            },
        },
        logo: {
            img: {
                css: css`
                    width: 50px !important;
                    height: auto !important;
                `,
            },
        },
        headerSearchInput: {
            searchInputStyles: {
                popoverContentBody: {
                    as: "div",
                    _height: "auto",
                    css: css`
                        display: inline-flex;
                        justify-content: flex-end;
                        border-radius: 3px;
                    `,
                },
                searchHistoryStyles: {
                    wrapper: {
                        css: css`
                            display: flex;
                            flex-direction: column;
                            width: 240px;
                            padding: 0 20px 15px 20px;
                        `,
                    },
                },
            },
        },
    }) as Styles;

// XNG-Changes: Change translateX to be 0 all the time
const transitionStyles: Dictionary<{ opacity: number; transform: string }> = {
    entering: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    entered: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exiting: { opacity: 1, transform: "translateY(0px) translateX(0)" },
    exited: { opacity: 0, transform: "translateY(-100%) translateX(0)" },
};

const InnerWrapper = styled.div<{ isEdit?: boolean } & InjectableCss>`
    display: grid;
    grid-template-columns: ${props => (props.isEdit ? "200px 200px auto 300px 100px" : "66px 50px auto 256px 60px")};
    padding: ${props => props.isEdit && "30px 10px"};
    width: 100%;
    margin: 0 auto;
    ${({ theme, isEdit }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return isEdit
            ? css`
                  max-width: 100%;
              `
            : breakpointMediaQueries(
                  theme,
                  maxWidths.map((mw: any) =>
                      mw
                          ? css`
                                max-width: ${mw}px;
                            `
                          : null,
                  ),
              );
    }}
    ${injectCss}
`;

const CompactHeader = (props: Props) => {
    const { isCurrentPage, isEditing, isInShell } = props.shellContext;
    const isShell = isCurrentPage && isInShell;
    const isEdit = isCurrentPage && isEditing && isInShell;
    const [isShow, setIsShow] = useState(false);
    const transitionElement = React.useRef<HTMLElement | null>(null);
    // XNG-Changes
    const searchInputRef = React.useRef<any>(null);

    useEffect(() => {
        if (!props.hideCompactHeader) return;
        setIsShow(false);
        props.xHeaderUpdate({ key: "hideCompactHeader", value: false });
    }, [props.hideCompactHeader]);

    useEffect(() => {
        function eventHandler() {
            const scrollPos = Math.round(window.scrollY);
            const windowWidth = Math.round(window.innerWidth);

            // XNG-Changes
            const triggerPoint = props.headerHeight
                ? Number(props.headerHeight)
                : Number(props.theme.header.triggerPoint);

            // XNG-Changes: use triggerPoint and isTablet
            if (scrollPos > triggerPoint && props.isTablet) {
                setIsShow(true);
            } else if (isShow && (scrollPos < triggerPoint || !props.isTablet) && !props.mobileSearch) {
                setIsShow(false);
            }

            // XNG-Changes
            props.xHeaderUpdate({ key: "isSticky", value: isShow });
        }
        window.addEventListener("scroll", eventHandler, { passive: true });

        return () => {
            window.removeEventListener("scroll", eventHandler);
        };
    }, [props.theme.header.triggerPoint, isShow, props.isTablet, props.mobileSearch]);

    useEffect(() => {
        setIsShow(Boolean(isShell));
    }, [isShell]);

    // XNG-Changes
    if (props.isCheckout) {
        return null;
    }

    // <XNG-Changes>
    const setMobileSearch = () => {
        // XNG-Changes: flushSync so the input is visible before focus (required for iOS Safari user-gesture focus)
        flushSync(() => {
            props.xHeaderUpdate({ key: "mobileSearch", value: true });
        });

        const inputEl = searchInputRef?.current as HTMLInputElement | null;
        if (inputEl) {
            inputEl.focus();
        }
    };
    const { mobileSearch, mainNavigationWidget, logoImageWidget, headerSignInWidget, cartLinkWidget } = props;
    // </XNG-Changes>

    // XNG-Changes: update structure
    return (
        <Transition nodeRef={transitionElement} in={isShow} timeout={0}>
            {state => (
                <Page
                    ref={transitionElement}
                    {...styles.page}
                    as="header"
                    background={props?.theme?.header?.backgroundColor}
                    style={{
                        transition: "all 350ms ease",
                        pointerEvents: isShow ? "auto" : "none",
                        ...transitionStyles[state],
                    }}
                    // XNG-changes
                    {...{ mobileSearch }}
                >
                    <StylesProvider styles={createStylesProviderStyles(props.theme)}>
                        <InnerWrapper isEdit={isEdit} {...styles.pageInnerWrapper} {...{ mobileSearch }}>
                            {!props.mobileSearch && (
                                <>
                                    <StyledWrapper {...styles.mainNavigationWrapper}>
                                        {createWidgetElement("Header/MainNavigation", {
                                            fields: { ...mainNavigationWidget },
                                            isCompactHeaderFlyoutMenu: true,
                                        } as any)}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.logoWrapper}>
                                        {createWidgetElement("Basic/Logo", {
                                            fields: {
                                                mobileSpecificImage: logoImageWidget?.logoImage,
                                                logoImage: logoImageWidget?.logoImage,
                                            },
                                        })}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.rightSectionWrapper}>
                                        <Button
                                            onClick={setMobileSearch}
                                            {...styles.searchButton}
                                            // XNG-Changes
                                            aria-label={translate("Search")}
                                        >
                                            <ButtonIcon src={Search} size={22} color="primary" />
                                        </Button>
                                        {createWidgetElement("Common/HeaderSignIn", {
                                            fields: {
                                                ...headerSignInWidget,
                                            },
                                        })}
                                        {createWidgetElement("Common/CartLink", {
                                            fields: {
                                                ...cartLinkWidget,
                                            },
                                        })}
                                    </StyledWrapper>
                                </>
                            )}

                            <StyledWrapper {...styles.searchWrapper} {...{ mobileSearch }}>
                                <SearchInput
                                    id={props.id}
                                    inputRef={searchInputRef}
                                    extendedStyles={styles.mobileSearchInputStyles}
                                />
                            </StyledWrapper>
                        </InnerWrapper>
                    </StylesProvider>
                </Page>
            )}
        </Transition>
    );
};

const pageModule: PageModule = {
    // XNG-Changes: add connect
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withIsInShell(CompactHeader))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
